import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    if (target) target.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    if (this.refs.zoomDialogComponent) {
      this.refs.zoomDialogComponent.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
        signal,
      });
    }

    requestAnimationFrame(() => {
      this.#ensurePrimaryImageResolution();
    });

    window.addEventListener('pageshow', this.#handlePageShow, { signal: this.#controller.signal });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by morphing or quickly switching gallery media.
   *
   * @param {Event} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail?.data?.html;
    const resource = event.detail?.resource;

    if (!source) return;

    if (resource) {
      this.#previewVariantMedia(resource);
      requestAnimationFrame(() => {
        this.#ensurePrimaryImageResolution();
      });
    }

    if (resource && this.#switchToVariantMedia(resource)) {
      return;
    }

    const newMediaGallery = source.querySelector('media-gallery');
    if (!newMediaGallery) return;

    requestAnimationFrame(() => {
      morph(this, newMediaGallery);
    });
  };

  #getPrimaryImage() {
    return (
      this.querySelector('.media-gallery__grid .product-media-container:first-child img.product-media__image') ||
      this.querySelector('.media-gallery__grid img.product-media__image') ||
      this.querySelector('slideshow-slide[aria-hidden="false"] img.product-media__image') ||
      this.querySelector('slideshow-slide:first-child img.product-media__image') ||
      this.querySelector('.product-media img.product-media__image')
    );
  }

  #getHighResolutionVariantUrl(variant, img) {
    const maxRes = img?.getAttribute('data_max_resolution');
    if (maxRes) return maxRes;

    const previewSrc = variant?.featured_media?.preview_image?.src;
    if (!previewSrc) return null;

    try {
      const url = new URL(previewSrc, window.location.origin);
      url.searchParams.set('width', '2400');
      return url.toString();
    } catch {
      return previewSrc;
    }
  }

  #handlePageShow = () => {
    requestAnimationFrame(() => {
      this.#ensurePrimaryImageResolution();
    });
  };

  #upgradeImageToMaxResolution(img) {
    if (!(img instanceof HTMLImageElement)) return;

    const maxRes = img.getAttribute('data_max_resolution');
    if (!maxRes || img.dataset.highResApplied === 'true') return;

    if ((img.currentSrc || img.src) === maxRes) {
      img.dataset.highResApplied = 'true';
      return;
    }

    const loader = new Image();
    loader.className = img.className;
    loader.alt = img.alt;
    loader.loading = img.loading;
    loader.setAttribute('data_max_resolution', maxRes);
    loader.dataset.highResApplied = 'true';

    for (const attr of img.attributes) {
      if (attr.name.startsWith('data-') && attr.name !== 'data_max_resolution') {
        loader.setAttribute(attr.name, attr.value);
      }
    }

    loader.onload = () => {
      img.replaceWith(loader);
    };
    loader.src = maxRes;
  }

  #ensurePrimaryImageResolution() {
    const isDesktop = window.matchMedia('(min-width: 1041px)').matches;

    if (isDesktop && this.presentation === 'stack') {
      const firstGridImage = this.querySelector(
        '.media-gallery__grid .product-media-container:first-child img.product-media__image'
      );
      this.#upgradeImageToMaxResolution(firstGridImage);
      return;
    }

    const primaryImage = this.#getPrimaryImage();
    if (primaryImage instanceof HTMLImageElement) {
      this.#upgradeImageToMaxResolution(primaryImage);
    }
  }

  /**
   * @param {object} variant
   */
  #previewVariantMedia(variant) {
    if (this.dataset.hideVariants !== 'true') return;

    const img = this.#getPrimaryImage();
    if (!(img instanceof HTMLImageElement)) return;

    const highResUrl = this.#getHighResolutionVariantUrl(variant, img);
    if (!highResUrl || img.src === highResUrl) return;

    img.src = highResUrl;
    if (variant.featured_media?.alt) {
      img.alt = variant.featured_media.alt;
    }

    const slideshow = this.slideshow;
    if (slideshow) {
      slideshow.select(0, undefined, { animate: false });
    }
  }

  /**
   * @param {object} variant
   * @returns {boolean}
   */
  #switchToVariantMedia(variant) {
    if (this.dataset.hideVariants === 'true') return false;

    const mediaId = variant?.featured_media?.id;
    if (!mediaId) return false;

    const slideshow = this.slideshow;
    const slides = this.querySelectorAll('slideshow-slide');

    if (slideshow && slides.length > 0) {
      const mediaIdString = String(mediaId);
      const targetSlide = Array.from(slides).find(
        (slide) => slide.getAttribute('slide-id') === mediaIdString
      );

      if (targetSlide) {
        const index = Array.from(slides).indexOf(targetSlide);
        if (index >= 0) {
          slideshow.select(index, undefined, { animate: false });
          return true;
        }
      }

      const firstSlide = slides[0];
      const firstSlideId = firstSlide?.getAttribute('slide-id');

      if (firstSlideId === mediaIdString) {
        slideshow.select(0, undefined, { animate: false });
        return true;
      }

      const previewSrc = this.#getHighResolutionVariantUrl(variant, firstSlide.querySelector('img'));
      if (firstSlide && previewSrc) {
        const img = firstSlide.querySelector('img');
        if (img instanceof HTMLImageElement) {
          img.src = previewSrc;
          if (variant.featured_media?.alt) img.alt = variant.featured_media.alt;
          slideshow.select(0, undefined, { animate: false });
          return true;
        }
      }
    }

    if (this.presentation === 'stack') {
      return Boolean(this.querySelector(`.product-media[data-media-id="${mediaId}"]`));
    }

    return false;
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {Event} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    if (this.slideshow) this.slideshow.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    if (this.refs.zoomDialogComponent) this.refs.zoomDialogComponent.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
